import React, {FunctionComponent, useEffect, useState} from "react";
import {useAppSelector} from "../../../../../Config/Hooks";
import {FindPowerVSIrradianceDataByEquipmentIdAndByHour} from "../InverterReactQuery";
import {useTheme} from "@mui/material";
import {ACard, AChartX, Period, PeriodSelector, Skeleton, XAxis, YAxis} from "@atiautomacao/ati-ui-library";
import {addMinutes, format, parseISO} from "date-fns";
import {roundNextHundred, truncateNumber} from "../../../../../Utils/NumberUtil";
import {PowerIrradiance} from "../../DashboardPowerPlantCharts";
import {isArray} from "lodash";
import DataNotFound from "../../../../../Shared/Components/DataNotFoundMessage";

/** Mesma cor de destaque do gêmeo em SGD-3639 / ActivePowerInvertersChart */
const DIGITAL_TWIN_SERIES_NAME = "Potência AC Gêmeo";
const DIGITAL_TWIN_COLOR = "#1E293B";
/** Cores fixas por série — evita colisão de palette quando o gêmeo entra no meio (QA). */
const POWER_AC_COLOR = "rgb(25, 118, 210)";
const IRRADIANCE_COLOR = "rgb(255,198,10)";

interface SeriesOption {
    name: string;
    type: string;
    unity: string;
    yAxisIndex?: number;
    data: any[];
    date?: any;
    showSymbol?: boolean;
    smooth?: boolean;
    z?: number;
    lineStyle?: { color: string; width: number; opacity: number };
    itemStyle?: { color: string };
}

const PowerIrradianceInverterChart: FunctionComponent<{ powerPlant: any, equipmentId: number, nominalPower: number}> = ({powerPlant,equipmentId, nominalPower}) => {

    const initialPeriod: Period = {
        groupBy: "day",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };
    const [period, setPeriod] = useState<Period>(initialPeriod);
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const { data, isLoading } = FindPowerVSIrradianceDataByEquipmentIdAndByHour(powerPlant.id, equipmentId, period.fromDateTime, period.toDateTime);
    const theme = useTheme();
    const [powerIrradiance,setPowerIrradiance]  = useState<PowerIrradiance[]>([]);


    const [xAxis, setXAxis] = useState<XAxis>();
    const [yAxis, setYAxis] = useState<Array<YAxis>>(Array<YAxis>());
    const [series, setSeries] = useState<Array<SeriesOption>>(Array<SeriesOption>());
    const isDay = (group:string)=>{
        return group === "day"
    }

    const callback = (params:any) => {
        let formatDate = period && period.groupBy.valueOf() === "week" ? 'dd/MM HH:mm' : 'HH:mm'
        let dateStr = format(new Date(params[0].axisValue), formatDate);

        let result = dateStr + '<br/>';
        params.forEach(function (item: any) {
            const unit = item.seriesName === "Irradiância" ? 'W/m²' : 'kW';
            result += item.marker + " " + item.seriesName + "&nbsp;&nbsp;&nbsp;&nbsp;" + "<strong>" + item.value[1] + " " + unit + "</strong>" + "<br/>";
        });

        return result;
    }

    /** Alinha timestamps do Influx à grade de 5 min (mesmo padrão SGD-3639). */
    const snapDateTimeToFiveMinutes = (dateTime: string): string => {
        const d = new Date(dateTime);
        if (Number.isNaN(d.getTime())) {
            return dateTime;
        }
        const ms = 5 * 60 * 1000;
        const snapped = new Date(Math.round(d.getTime() / ms) * ms);
        return format(snapped, 'yyyy-MM-dd HH:mm:ss.S');
    };

    const adjustDataPoints = (dataPoints: any[]): any[] => {
        if (dataPoints.length === 0) {
            return dataPoints;
        }

        // Parseia o primeiro e último dateTime para objetos Date
        const firstDateTime = parseISO(dataPoints[0].dateTime);
        const lastDateTime = parseISO(dataPoints[dataPoints.length - 1].dateTime);

        // Horários limite
        const startTime = new Date(firstDateTime);
        startTime.setHours(4, 0, 0, 0); // 4:00
        const endTime = new Date(lastDateTime);
        endTime.setHours(20, 0, 0, 0); // 20:00

        let newData = [...dataPoints]
        while (firstDateTime > startTime) {
            const newDateTime = addMinutes(firstDateTime, -5);
            newData.unshift({
                dateTime: format(newDateTime, 'yyyy-MM-dd HH:mm:ss.S'),
                activePower: null,
                irradiance: null,
            });
            firstDateTime.setTime(newDateTime.getTime());
        }

        while (lastDateTime < endTime) {
            const newDateTime = addMinutes(lastDateTime, 5);
            newData.push({
                dateTime: format(newDateTime, 'yyyy-MM-dd HH:mm:ss.S'),
                activePower: null,
                irradiance: null,
            });
            lastDateTime.setTime(newDateTime.getTime());
        }

        return newData;
    }
    const hoursToMilliseconds = (hours:number): number => {
        return hours * 60 * 60 * 1000
    };

    const minDate = (minDate:string, group:string) : string | undefined => {
        if(!isDay(group)) return undefined;
        const min = new Date(minDate);
        min.setHours(4, 0, 0, 0); // 4:00
        return format(min, 'yyyy-MM-dd HH:mm:ss.S')
    }

    const maxDate = (maxDate:string, group:string) : string | undefined => {
        if(!isDay(group)) return undefined;
        const max = new Date(maxDate);
        max.setHours(20, 0, 0, 0);
        return format(max, 'yyyy-MM-dd HH:mm:ss.S')
    }

    const findPowerDataByPeriod = () => {
        if(period.groupBy){
            const grouping = period.groupBy.valueOf()
            const inverterRows = (powerIrradiance || []).filter((item: any) => item.digitalTwin !== true);
            const twinRows = (powerIrradiance || []).filter((item: any) => item.digitalTwin === true);
            const newData = isDay(grouping) ? adjustDataPoints(inverterRows) : inverterRows;
            let xAxisName: string= 'Dia';
            if (isDay(grouping)) {
                xAxisName = 'Horário';
            }

            const axisSource = newData.length > 0 ? newData : twinRows;
            setXAxis({
                name: xAxisName,
                type:'time',
                min: minDate(axisSource[0]?.dateTime, period.groupBy.valueOf()) ,
                max: maxDate(axisSource[0]?.dateTime, period.groupBy.valueOf()) ,
                nameLocation: 'middle',
                minInterval: isDay(grouping) ? hoursToMilliseconds(3) : hoursToMilliseconds(24),
                axisLabel: {
                    formatter: isDay(grouping) ? '{HH}:{mm}' : '{dd}/{MM}'
                },
                nameGap: 30
            });

            const percentage = 0.15;
            const capacity = nominalPower && nominalPower > 0 ? nominalPower : 1000;
            const capacityMaxLeftY = truncateNumber(percentage * capacity + (capacity));
            const irradianceSeed = newData.length > 0 ? newData[0] : { irradiance: 0 };
            const valueMaxIrradiance = newData.length > 0
                ? newData.reduce((max, obj) => ((obj.irradiance ?? 0) > (max.irradiance ?? 0) ? obj : max), irradianceSeed).irradiance
                : 0;
            const defaultRightY = valueMaxIrradiance > 1200 ? roundNextHundred(valueMaxIrradiance) : 1200;
            setYAxis([
                {
                    name: 'kW',
                    max: capacityMaxLeftY,
                    min: 0,
                    interval: truncateNumber(capacityMaxLeftY / 4),
                    nameLocation: 'middle',
                    nameGap: 60,
                },
                {
                    name: 'W/m²',
                    max: defaultRightY,
                    min: 0,
                    interval: roundNextHundred(defaultRightY / 4),
                    nameLocation: 'middle',
                    nameGap: 60,
                }
            ])

            const nextSeries: SeriesOption[] = [
                    {
                        name: 'Potência AC',
                        data: Array.isArray(newData) ? newData.map((item) => item.activePower != null ? [item.dateTime ? item.dateTime : "", truncateNumber(item.activePower, 2)] : [item.dateTime ? item.dateTime : "", "-"]) : [],
                        type: 'line',
                        unity: 'Kw',
                        showSymbol: false,
                        yAxisIndex: 0,
                        lineStyle: { color: POWER_AC_COLOR, width: 1, opacity: 1 },
                        itemStyle: { color: POWER_AC_COLOR },
                    },
                    {
                        name: 'Irradiância',
                        data: Array.isArray(newData) ? newData.map((item) => item.irradiance != null ?  [item.dateTime ? item.dateTime : "", truncateNumber(item.irradiance, 2)]: [item.dateTime ? item.dateTime : "","-"]) : [],
                        type: 'line',
                        unity: 'W/m²',
                        showSymbol: false,
                        yAxisIndex: 1,
                        lineStyle: { color: IRRADIANCE_COLOR, width: 1, opacity: 1 },
                        itemStyle: { color: IRRADIANCE_COLOR },
                    }
                ];

            if (twinRows.length > 0) {
                const twinMapped = twinRows.map((item: any) => {
                    const dt = isDay(grouping)
                        ? snapDateTimeToFiveMinutes(item.dateTime)
                        : item.dateTime;
                    return item.activePower != null
                        ? [dt || "", truncateNumber(item.activePower, 2)]
                        : [dt || "", "-"];
                });
                nextSeries.splice(1, 0, {
                    name: DIGITAL_TWIN_SERIES_NAME,
                    data: twinMapped,
                    type: 'line',
                    unity: 'Kw',
                    showSymbol: false,
                    yAxisIndex: 0,
                    smooth: true,
                    z: 10,
                    lineStyle: { color: DIGITAL_TWIN_COLOR, width: 2, opacity: 0.6 },
                    itemStyle: { color: DIGITAL_TWIN_COLOR },
                });
            }

            setSeries(nextSeries);
        }

    }
    useEffect(() => {
        if (equipmentId && powerPlant.id) {
            if (data) {
                setPowerIrradiance(data?.data || []); // Atualiza os dados no estado
            }
        } else {
            setPowerIrradiance([]);
        }
    }, [equipmentId, powerPlant.id, data]);

    useEffect(() => {
        if (isArray(powerIrradiance) && powerIrradiance.length > 0 && period.groupBy){
            findPowerDataByPeriod();
        }else{
            setSeries([])
            setXAxis(undefined)
            setYAxis([])
        }
    }, [powerIrradiance, period.groupBy]);


    // @ts-ignore
    const option: AChartXProps['option'] = {
        // Fallback; cada série já define itemStyle/lineStyle (gêmeo não depende deste array).
        color: [POWER_AC_COLOR, IRRADIANCE_COLOR],
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    formatter: function (params: any) {
                        const formatDate = period.groupBy.valueOf() === "week" ? "dd/MM HH:mm" : "HH:mm"

                        if (params.axisDimension === 'x') {
                            return format(new Date(params.value), formatDate);
                        }
                        if (params.axisDimension === 'y') {
                            return truncateNumber(params.value,2);
                        }
                    }
                }
            },
            formatter: callback
        },
        toolbox: {
            right: '15%',
            top: -8,
            itemSize: 15,
            feature: {
                dataZoom: {
                    yAxisIndex: 'none'
                },
                magicType: { show: false, type: ['line', 'bar'] },
                restore: { show: true }
            }
        },
        dataZoom: [
            {
                type: 'inside',
                throttle: 50
            },
        ],
        legend: {
            show: true,
            type: 'scroll',
            orient: 'horizontal', // Alterado para orientação horizontal
            bottom: -6, // Posiciona a legenda abaixo do gráfico
            left: 'center', // Centraliza a legenda horizontalmente
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            textStyle: {
                opacity: 1
            },
            itemStyle: {
                opacity: 1
            },
            data: series.map(el => el.name)
        },
        grid: {
            containLabel: true,
            top: 10,
            bottom: 40,
        },
        label: {
            precision: 1,
            position: 'right',
            valueAnimation: true,
            fontFamily: 'monospace'
        },
        animationDuration: 2000,
        xAxis: xAxis,
        yAxis: yAxis,
        series: series,
    };


    let content;
    if (isLoading) {
        content = <Skeleton animation="wave" height={289} variant="rounded" width="100%" />;
    } else if (isArray(powerIrradiance) && powerIrradiance.length > 0) {
        content = (
            <AChartX
                key={`chart-power-irradiance-${openSubMenu}`}
                option={option}
                width={'100%'}
                height={289}
                loading={false}
                theme={theme.palette.mode}
            />
        );
    } else {
        content = <DataNotFound boxStyle={{ height: 289, width: '100%' }} />;
    }


    return (
        <ACard
            ariaLabel={"dashboard-station-inverter-power-x-irradiance"}
            key={`chart-power-irradiance`}
            styleProps={{
                cardStyle: { height: 445 },
                contentStyle: { padding: 0, marginTop: 70 }
            }}
            title={"Potência x Irradiância"}
            headerControls={
                <PeriodSelector
                    styleProps={{ base: { flexDirection: "row", display: "flex", alignItems: "center" } }}
                    mode="hybrid"
                    hideDatetime={false}
                    hideGroup={false}
                    disableHourSelector={true}
                    inputFormat={'dd/MM/yyyy'}
                    period={period}
                    periodInterval={{
                        day: {date: period.toDateTime, startTime: "00:00:00", endTime: "23:59:59"},
                        week: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 6}
                    }}
                    groupByOptions={["day", "week"]}
                    onChange={setPeriod}
                />
            }

        >
            {content}
        </ACard>
    );
}

export default PowerIrradianceInverterChart;
